import { computed } from 'vue'
import { useStore } from 'vuex'

/**
 * Shared store bindings for item view composables.
 * Extracts the common getters, mutations, actions, and dispatch wrappers
 * from the collectionItems store that are duplicated across
 * useCollectionItemView, useCollectionContentView, and usePublicCollectionContentView.
 *
 * @param {Object} [options]
 * @param {string} [options.filterGetter='filters'] - Store getter for filters ('filters' or 'itemsFilters')
 * @param {string} [options.defaultFilterGetter='defaultFilterFn'] - Store getter for default filters
 */
export const useItemStoreState = ({
  filterGetter = 'filters',
  defaultFilterGetter = 'defaultFilterFn',
} = {}) => {
  const store = useStore()

  // ==================== GETTERS ====================
  const userId = computed(() => store.getters['auth/userId'])
  const collectionId = computed(() => store.getters['collectionItems/collectionId'])
  const listStyle = computed(() => store.getters['collectionItems/listStyle'])
  const isExtensionPopup = computed(() => store.getters['device/isExtensionPopup'])
  const isDesktop = computed(() => store.getters['device/isDesktop'])
  const isMobile = computed(() => store.getters['device/isMobile'])
  const itemShow = computed(() => store.getters['collectionItems/show'])
  const itemNext = computed(() => store.getters['collectionItems/next'])
  const itemFilters = computed(() => store.getters[`collectionItems/${filterGetter}`])
  const itemDefaultFilterFn = computed(() => store.getters[`collectionItems/${defaultFilterGetter}`])
  const sorting = computed(() => store.getters['collectionItems/sorting'])
  const collectionListStyle = computed(() => store.getters['collectionItems/collectionListStyle'])
  const itemCount = computed(() => store.getters['collectionItems/itemCount'])
  const filteredItemCount = computed(() => store.getters['collectionItems/filteredItemCount'])
  const isFilterSidebarOpen = computed(() => store.getters['collectionItems/filterOpen'])
  const importQueueSize = computed(() => store.getters['collectionItems/importQueueSize'])
  const sumImportQueueSize = computed(() => store.getters['collectionItems/sumImportQueueSize'])
  const lastImportSize = computed(() => store.getters['collectionItems/lastImportSize'])
  const activeImport = computed(() => store.getters['collectionItems/activeImport'])
  const hasActiveFilters = computed(() => store.getters['bulkActionsV2/hasActiveFilters'])

  // ==================== MUTATIONS ====================
  const setCollectionId = val => store.commit('collectionItems/setCollectionId', val)
  const setListStyle = val => store.commit('collectionItems/setListStyle', val)
  const setItemShow = val => store.commit('collectionItems/setShow', val)
  const setItemNext = val => store.commit('collectionItems/setNext', val)
  const setCollectionListStyle = val => store.commit('collectionItems/setCollectionListStyle', val)
  const setFilterSidebarOpen = val => store.commit('collectionItems/setFilterOpen', val)
  const setFilteredItemCount = val => store.commit('collectionItems/setFilteredItemCount', val)
  const reset = () => store.commit('collectionItems/reset')

  // ==================== ACTIONS ====================
  const setItemFilters = val => store.dispatch('collectionItems/setFilters', val)
  const setItemSorting = val => store.dispatch('collectionItems/setSorting', val)
  const setFilterTags = val => store.dispatch('collectionItems/setFilterTags', val)

  // ==================== DISPATCH WRAPPERS ====================
  const getPinnedItems = () => store.dispatch('collectionItems/getPinnedItems')
  const getItems = () => store.dispatch('collectionItems/getItems')
  const getItemStats = () => store.dispatch('collectionItems/getStats')
  const getPopularTags = () => store.dispatch('collectionItems/getPopularTags')

  // ==================== CONVENIENCE METHODS ====================
  const onOpenFilter = () => setFilterSidebarOpen(true)
  const resetFilters = () => setItemFilters(itemDefaultFilterFn.value())
  const setSorting = val => setItemSorting(val)

  const searchValue = computed({
    get: () => itemFilters.value?.search || '',
    set: search => setItemFilters({ ...itemFilters.value, search }),
  })

  return {
    store,

    // Getters
    userId,
    collectionId,
    listStyle,
    isExtensionPopup,
    isDesktop,
    isMobile,
    itemShow,
    itemNext,
    itemFilters,
    itemDefaultFilterFn,
    sorting,
    collectionListStyle,
    itemCount,
    filteredItemCount,
    isFilterSidebarOpen,
    importQueueSize,
    sumImportQueueSize,
    lastImportSize,
    activeImport,
    hasActiveFilters,

    // Mutations
    setCollectionId,
    setListStyle,
    setItemShow,
    setItemNext,
    setCollectionListStyle,
    setFilterSidebarOpen,
    setFilteredItemCount,
    reset,

    // Actions
    setItemFilters,
    setItemSorting,
    setFilterTags,

    // Dispatch wrappers
    getPinnedItems,
    getItems,
    getItemStats,
    getPopularTags,

    // Convenience
    onOpenFilter,
    resetFilters,
    setSorting,
    searchValue,
  }
}
